import { isPlatformBrowser } from '@angular/common';
import {DOCUMENT, effect, inject, Injectable, PLATFORM_ID, signal} from '@angular/core';

export type ModalClickEvent = (btn: IModalButton, event: MouseEvent, component?: any) => void;

export interface IModalButton {
  id?: string;
  text: string;
  primary?: boolean;
  close?: boolean;
  click?: ModalClickEvent;
}

export interface IModalData {
  title?: string;
  message?: string;
  component?: any;
  loadComponent?: () => Promise<any>;
  inputs?:  { [key: string]: any };
  outputs?: { [key: string]: (...args: any[]) => void };
  buttons?: IModalButton[];
  styles?: IModalStyles;
  class?: string;
}

export interface IModalStyles {
  leftButtons?: boolean;
  fullscreen?: boolean;
  noBackdrop?: boolean;
  fullscreenTop?: number; // Use with fullscreen to make space for site header if needed
  sidebar?: boolean; // Renders the modal as a right-edge sliding drawer instead of a centered dialog
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private modalList = signal<IModalData[]>([]);
  modals = this.modalList.asReadonly();

  private readonly platformId = inject(PLATFORM_ID);
  private readonly documentRef = inject(DOCUMENT, { optional: true }) as Document | null;
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // Effect to disable scroll when modal with fullscreen style is opened
  private readonly fxDisableScroll = effect(() => {
    if (!this.isBrowser || !this.documentRef) {
      return;
    }
    const fullscreen = !!this.modals().some(data => data?.styles?.fullscreen);
    if (fullscreen) {
      this.disableScroll();
    } else {
      this.enableScroll();
    }
  });

  async show(data: IModalData | string) {
    if (typeof data === 'string') {
      void this.show({
        message: data,
        buttons: [
          {text: 'Close', close: true, primary: true}
        ]
      });
      return;
    }
    if (this.isModalExists(data)) {
      return;
    }

    const done = () => {
      this.modalList.update(m => [...m, data]);
      // this.onModalsChanged.emit(this.modals);
    }

    // Check if lazy loading
    if (data.loadComponent) {
      data.component = await data.loadComponent();
    } /*else if (data.component && data.component['then']) {
      const module = await data.component;
      data.component = module[Object.keys(module)[0]];
    }*/
    done();
    return data;
  }

  close(data: IModalData) {
    const idx = this.modalList().indexOf(data);
    if (idx !== -1) {
      this.modalList.update(m => {
        const newModals = [...m];
        newModals.splice(idx, 1);
        return newModals;
      });
      //this.onModalsChanged.emit(this.modals);
    }
  }

  showError(error: any, message = '') {
    let e = error;
    if (typeof e === 'string') {
      try {
        e = JSON.parse(error);
      } catch {
      }
    }
    if (e.error?.summary) {
      void this.show(message + e.error.summary);
    } else {
      if (e.summary) {
        void this.show(message + e.summary);
      } else {
        if (e.message) {
          void this.show(message + e.message);
        } else {
          if (typeof e === 'string') {
            void this.show(message + e);
          }
        }
      }
    }
  }

  private isModalExists(data: IModalData) {
    return this.modals().includes(data) ||
      this.modals().some(m => {
        return m.title === data.title &&
          m.message === data.message &&
          m.buttons?.length === data.buttons?.length
      });
  }

  async showConfirmDialog(message: string, confirmCallback: () => void, cancelCallback?: () => void) {
    return this.show({
      message,
      buttons: [
        {id: 'yes', text: 'Yes', close: true, primary: true, click: () => confirmCallback() },
        {id: 'no', text: 'No', close: true, click: () => cancelCallback ? cancelCallback() : undefined }
      ]
    })
  }

  private disableScroll() {
    this.documentRef?.body.classList.add('ui-no-scroll');
  }

  private enableScroll() {
    this.documentRef?.body.classList.remove('ui-no-scroll');
  }
}
